// react
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useNavigate, useParams } from 'react-router-dom';
import { DYNAMIC_ROUTES } from 'router';
import { useGoBack } from 'hooks/useGoBack';

// store
import {
  useCreateManyAnswerMutation,
  useGetAllCategoryByProfileQuery,
  useSubmitCareProfileFormMutation,
} from 'store/api/careProfile';

// tracking
import {
  TrackingForm,
  TrackingFormEventType,
  useTracking,
  usePermissions,
} from 'hooks';

// models
import {
  categoryByID,
  buildForms,
  answersFromMap,
  buildSections,
  subQuestionsFromCategory,
  answersFromQuestions,
  listAllSubQuestionsOfForm,
  formFromCategory,
  CareProfileLabeledAnswer,
  CareProfileCategoryID,
} from '@karehero/models';
import { careProfileDocDefinition } from 'pdf';
import { getCurrentCareCircle } from 'store/slices/careCircleSlice';
import { useSelector } from 'store';
import { useGetCareCircleQuery } from 'store/api/careCircle';

interface UseCareProfileOptions {
  careCircleID?: string;
  categoryID: CareProfileCategoryID;
  formID?: string;
  isSkipAnswers?: boolean;
}

export const useCareProfile = (options?: UseCareProfileOptions) => {
  // hooks
  const { trackForm } = useTracking();
  const navigate = useNavigate();

  let careCircleId = options?.careCircleID;
  const sessionCareCircle = useSelector(getCurrentCareCircle);
  if (!options?.careCircleID) {
    careCircleId = sessionCareCircle?.id;
  }

  const { data: careCircle } = useGetCareCircleQuery(careCircleId || '', {
    skip: !careCircleId,
  });

  const { categoryID } = options || {};

  const careProfileID = careCircle?.careProfile?.id || '';

  const { data: careProfileCategories } = useGetAllCategoryByProfileQuery(
    careProfileID,
    {
      skip: !careProfileID,
    },
  );

  const careRecipientFirstName = useMemo(
    () => careCircle?.careRecipientAccount?.firstName,
    [careCircle],
  );

  const careRecipientName = useMemo(() => {
    let name = `${careCircle?.careRecipientAccount?.firstName || ''}`;
    if (careCircle?.careRecipientAccount?.lastName) {
      name += ` ${careCircle?.careRecipientAccount?.lastName}`;
    }
    return name;
  }, [careCircle]);

  const [submitCareProfileForm] = useSubmitCareProfileFormMutation();

  const [createManyAnswer, { isLoading: isCreatingAnswers }] =
    useCreateManyAnswerMutation();

  const { permissionByFormCategoryID } = usePermissions();

  // state
  const [answers, setAnswers] = useState<{
    [id: string]: string;
  }>({});

  // memos
  const category = useMemo(() => {
    if (!careProfileCategories || !(categoryID || '' in CareProfileCategoryID))
      return;

    return categoryByID(careProfileCategories, categoryID);
  }, [careProfileCategories, categoryID]);

  const questions = useMemo(
    () => category && subQuestionsFromCategory(category),
    [category],
  );

  const savedAnswers = useMemo(
    () => questions && answersFromQuestions(questions),
    [questions],
  );

  const forms = useMemo(
    () => category && answers && buildForms(category, answers),
    [category, answers],
  );

  const sections = useMemo(
    () => (category && forms && buildSections(category, forms)) || [],
    [category, forms],
  );

  const title = useMemo(
    () => `${careRecipientFirstName || 'Your Loved One'}'s ${category?.title}`,
    [category, careRecipientFirstName],
  );

  const toFirstFormPage = useMemo(
    () =>
      DYNAMIC_ROUTES.CARE_PROFILE_CATEGORY_FORM(
        category?.id,
        category?.firstFormID,
      ),
    [category],
  );

  const toLastFormPage = useMemo(
    () =>
      DYNAMIC_ROUTES.CARE_PROFILE_CATEGORY_FORM(
        category?.id,
        category?.finalFormID,
      ),
    [category],
  );

  const progressByID = useMemo(() => {
    const map: Record<string, number> = {};
    careProfileCategories?.forEach((cat) => {
      const required = cat.progress?.requiredCount || 0;
      const answered = cat.progress?.answeredCount || 0;
      map[cat.id] = required > 0 ? Math.round((answered / required) * 100) : 0;
    });
    return map;
  }, [careProfileCategories]);

  const permission = useMemo(
    () => permissionByFormCategoryID(categoryID || ''),
    [categoryID, permissionByFormCategoryID],
  );

  const formType = useMemo(() => {
    return (
      (category &&
        {
          [CareProfileCategoryID.AttendanceAllowance]:
            TrackingForm.AttendanceAllowance,
          [CareProfileCategoryID.CareAssessment]: TrackingForm.CareAssessment,
          [CareProfileCategoryID.FinancialStatus]: TrackingForm.FinancialStatus,
          [CareProfileCategoryID.EligibilityCalculator]:
            TrackingForm.EligibilityCalculator,
        }[category.id]) ||
      TrackingForm.CareAssessment
    );
  }, [category]);

  // callbacks
  const createManyAnswerWithProfileID = useCallback(
    (a: { [id: string]: string }) =>
      a &&
      createManyAnswer({
        careProfileId: careProfileID,
        answers: answersFromMap(careProfileID, a),
      }),
    [createManyAnswer, careProfileID],
  );

  const handlePrintPdf = useCallback(
    (
      profileAnswers?: CareProfileLabeledAnswer[],
      recipientName?: string,
      isSummary: boolean = false,
    ) => {
      if (!category) return;

      const answersToUse =
        profileAnswers && profileAnswers.length > 0 ? profileAnswers : answers;

      careProfileDocDefinition(
        [category],
        answersToUse,
        recipientName || careRecipientName,
        category.id,
        category.title,
        isSummary,
      ).open();
    },
    [category, careRecipientName, answers],
  );

  const trackCompleted = useCallback(() => {
    trackForm(formType, TrackingFormEventType.Completed, {});
  }, [trackForm, formType]);

  const trackStarted = useCallback(() => {
    trackForm(formType, TrackingFormEventType.Started, {});
  }, [trackForm, formType]);

  const trackSubmitted = useCallback(() => {
    trackForm(formType, TrackingFormEventType.Submitted, {});
  }, [trackForm, formType]);

  const handleSubmit = useCallback(() => {
    if (
      category?.careProfileFormEvents
        ?.filter((ev) => ev.eventType === 'form-submitted')
        ?.find((ev) => {
          if (!ev?.submittedAt) return false;

          const now = new Date();

          // Return the event if it was submitted within the last day
          return (
            new Date(ev.submittedAt) >=
            new Date(now.getTime() - 24 * 60 * 60 * 1000)
          );
        })
    ) {
      // The submission event has occurred in the last 24 hours
      // Skip tracking the event to prevent any bounces
      return;
    }

    trackSubmitted();
    careProfileID &&
      category &&
      submitCareProfileForm({
        careProfileId: careProfileID,
        careProfileCategoryId: category?.id,
        eventType: 'form-submitted',
      });
  }, [trackSubmitted, submitCareProfileForm, careProfileID, category]);

  const handleStart = useCallback(() => {
    if (!careProfileID || !category || !toFirstFormPage) return;

    trackStarted();
    submitCareProfileForm({
      careProfileId: careProfileID,
      careProfileCategoryId: category?.id,
      eventType: 'form-started',
    });
    navigate(toFirstFormPage);
  }, [
    trackStarted,
    navigate,
    submitCareProfileForm,
    careProfileID,
    category,
    toFirstFormPage,
  ]);

  // effects
  useEffect(() => {
    savedAnswers && setAnswers?.(savedAnswers);
  }, [savedAnswers]);

  return {
    careProfileID,
    title,
    formType,
    answers,
    category,
    forms,
    sections,
    careRecipientFirstName,
    careRecipientName,
    toFirstFormPage,
    toLastFormPage,
    permission,
    progressByID,
    isCreatingAnswers,
    setAnswers,
    createManyAnswer: createManyAnswerWithProfileID,
    trackCompleted,
    trackSubmitted,
    handleStart,
    handleSubmit,
    handlePrintPdf,
  };
};

export const useCareProfileForm = (options?: UseCareProfileOptions) => {
  const resp = useCareProfile(options);

  const {
    forms,
    category,
    formType,
    createManyAnswer,
    answers: allAnswers,
    setAnswers: setAllAnswers,
    isCreatingAnswers,
  } = resp;

  const { careProfileFormId: careProfileFormIdParams } = useParams();
  const navigate = useNavigate();
  const { trackForm } = useTracking();

  const goBack = useGoBack();

  const careProfileFormId = useMemo(
    () => options?.formID || careProfileFormIdParams,
    [careProfileFormIdParams, options],
  );

  const profileForm = useMemo(
    () => category && formFromCategory(category, careProfileFormId || ''),
    [category, careProfileFormId],
  );

  const formQuestions = useMemo(
    () => profileForm && listAllSubQuestionsOfForm(profileForm),
    [profileForm],
  );

  const form = useMemo(
    () => forms?.find((form) => form.id === careProfileFormId),
    [forms, careProfileFormId],
  );

  const answers = useMemo(() => {
    let a: { [k: string]: string } = {};
    formQuestions?.forEach((q) => {
      const ans = allAnswers[q.id];
      if (ans !== undefined) {
        a[q.id] = ans;
      }
    });
    return a;
  }, [formQuestions, allAnswers]);

  const toPrevious = useMemo(() => {
    return form?.prevFormID
      ? DYNAMIC_ROUTES.CARE_PROFILE_CATEGORY_FORM(
          category?.id,
          form?.prevFormID,
        )
      : DYNAMIC_ROUTES.CARE_PROFILE_CATEGORY(category?.id);
  }, [category?.id, form, form?.prevFormID]);

  const toNext = useMemo(() => {
    return DYNAMIC_ROUTES.CARE_PROFILE_CATEGORY_FORM(
      category?.id,
      form?.nextFormID || 'complete',
    );
  }, [category?.id, form, form?.nextFormID]);

  // mixpanel events
  const trackViewed = useCallback(() => {
    trackForm(formType, TrackingFormEventType.Viewed, {
      carePlanFormId: careProfileFormId,
    });
  }, [trackForm, formType, careProfileFormId]);

  const trackSaved = useCallback(() => {
    trackForm(formType, TrackingFormEventType.Saved, {
      carePlanFormId: careProfileFormId,
    });
  }, [trackForm, formType, careProfileFormId]);

  // callbacks
  const setAnswers = useCallback(
    (newAnswers: { [key: string]: string }) => {
      setAllAnswers({ ...allAnswers, ...newAnswers });
    },
    [allAnswers, setAllAnswers],
  );

  // Stay disabled after save/cancel until the next form page mounts.
  const [isSubmitting, setIsSubmitting] = useState(false);
  const prevFormIdRef = useRef(careProfileFormId);
  if (careProfileFormId !== prevFormIdRef.current) {
    prevFormIdRef.current = careProfileFormId;
    if (isSubmitting) setIsSubmitting(false);
  }

  const handleSave = useCallback(async () => {
    setIsSubmitting(true);
    try {
      await createManyAnswer(answers);
      trackSaved();
      navigate(toNext, { replace: true, state: { back: false } });
    } catch {
      setIsSubmitting(false);
    }
  }, [createManyAnswer, navigate, answers, toNext, trackSaved]);

  const handleCancel = useCallback(() => {
    setIsSubmitting(true);
    if (!form?.prevFormID) {
      goBack();
    } else {
      navigate(toPrevious, { replace: true, state: { back: true } });
    }
  }, [navigate, goBack, form?.prevFormID, toPrevious]);

  const progress = category ? resp.progressByID[category.id] || 0 : 0;

  return {
    ...resp,
    form,
    answers,
    careProfileFormId,
    toPrevious,
    toNext,
    isSubmitting,
    isCreatingAnswers,
    setAnswers,
    trackViewed,
    trackSaved,
    handleSave,
    handleCancel,
    progress,
  };
};
